import React, { useState } from 'react';
import { PRESET_PROFILES, COMPANY_SIZE_OPTIONS } from '../../constants';
import Button from '../common/Button';
import Icon from '../common/Icon';

interface ProfileGeneratorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onGenerate: (
    name: string,
    title: string,
    industry: string,
    companySize: string,
    segment: string,
    objective: string
  ) => Promise<void>;
  isGenerating: boolean;
}

const ProfileGeneratorModal: React.FC<ProfileGeneratorModalProps> = ({
  isOpen,
  onClose,
  onGenerate,
  isGenerating,
}) => {
  const [name, setName] = useState('Sarah Jenkins');
  const [title, setTitle] = useState('Chief Revenue Officer');
  const [industry, setIndustry] = useState('Financial Technology');
  const [companySize, setCompanySize] = useState('501-1000 Employees');
  const [segment, setSegment] = useState('Enterprise SaaS');
  const [objective, setObjective] = useState('Initial Market Expansion');

  const applyPreset = (presetName: keyof typeof PRESET_PROFILES) => {
    const preset = PRESET_PROFILES[presetName];
    setName(preset.name);
    setTitle(preset.title);
    setIndustry(preset.industry);
    setCompanySize(preset.companySize);
    setSegment(preset.segment);
    setObjective(preset.objective);
  };

  const handleGenerate = () => {
    onGenerate(name, title, industry, companySize, segment, objective);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-brand-black/95 backdrop-blur-md flex items-center justify-center px-6 py-[30px] animate-in fade-in duration-300">
      <div className="bg-white rounded-brand w-full max-w-4xl flex flex-col max-h-full overflow-hidden relative shadow-[0_0_100px_rgba(250,98,132,0.4)]">
        <div className="p-10 overflow-y-auto flex-1">
          <div className="flex justify-between items-start mb-10">
            <div>
              <h2 className="text-4xl font-extrabold text-brand-black mb-3 leading-none">
                Intelligence Profile Builder
              </h2>
              <p className="text-sm font-bold text-brand-pink uppercase tracking-widest">
                Model: Gemini 3 Pro (Deep Synthesis)
              </p>
            </div>
            <button
              onClick={onClose}
              className="w-12 h-12 rounded-full bg-brand-grey01 flex items-center justify-center hover:bg-brand-pink hover:text-white transition-all"
            >
              <Icon name="close" size="lg" />
            </button>
          </div>

          <div className="mb-7">
            <label className="block text-[10px] font-black uppercase mb-4 tracking-[0.1em] text-brand-grey08">
              Quick Presets
            </label>
            <div className="flex flex-wrap gap-3">
              {(Object.keys(PRESET_PROFILES) as Array<keyof typeof PRESET_PROFILES>).map((preset) => (
                <button
                  key={preset}
                  onClick={() => applyPreset(preset)}
                  className="px-6 py-3 border-2 border-brand-grey01 rounded-pill text-[11px] font-black uppercase tracking-wider hover:border-brand-pink hover:text-brand-pink transition-all bg-white"
                >
                  {preset}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <label className="block text-[11px] font-black uppercase mb-3 tracking-widest text-brand-grey08">
                  Name
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full border-2 border-brand-grey01 rounded-field p-4 text-xs font-bold outline-none focus:border-brand-pink transition-colors bg-brand-grey01/20"
                />
              </div>
              <div>
                <label className="block text-[11px] font-black uppercase mb-3 tracking-widest text-brand-grey08">
                  Title
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full border-2 border-brand-grey01 rounded-field p-4 text-xs font-bold outline-none focus:border-brand-pink transition-colors bg-brand-grey01/20"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <label className="block text-[11px] font-black uppercase mb-3 tracking-widest text-brand-grey08">
                  Industry
                </label>
                <input
                  type="text"
                  value={industry}
                  onChange={(e) => setIndustry(e.target.value)}
                  className="w-full border-2 border-brand-grey01 rounded-field p-4 text-xs font-bold outline-none focus:border-brand-aqua transition-colors bg-brand-grey01/20"
                />
              </div>
              <div>
                <label className="block text-[11px] font-black uppercase mb-3 tracking-widest text-brand-grey08">
                  Size
                </label>
                <select
                  value={companySize}
                  onChange={(e) => setCompanySize(e.target.value)}
                  className="w-full border-2 border-brand-grey01 rounded-field p-4 text-xs font-bold outline-none focus:border-brand-aqua transition-colors bg-brand-grey01/20"
                >
                  {(COMPANY_SIZE_OPTIONS || []).map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-[11px] font-black uppercase mb-3 tracking-widest text-brand-grey08">
                Campaign Objective
              </label>
              <textarea
                value={objective}
                onChange={(e) => setObjective(e.target.value)}
                className="w-full border-2 border-brand-grey01 rounded-field p-4 text-xs font-bold outline-none focus:border-brand-pink transition-colors h-32 resize-none bg-brand-grey01/20"
                placeholder="e.g. Sell data modernization to high-churn accounts"
              />
            </div>

            <Button
              variant="primary"
              size="lg"
              icon={isGenerating ? undefined : 'psychology'}
              onClick={handleGenerate}
              isLoading={isGenerating}
              className="w-full uppercase tracking-widest shadow-2xl"
            >
              {isGenerating ? 'Synthesizing Persona...' : 'Build Intelligence Profile'}
            </Button>
          </div>
        </div>

        <div className="bg-brand-grey01 px-12 py-6 flex items-center gap-5 border-t border-brand-grey02">
          <Icon name="info" className="text-brand-pink text-3xl" />
          <p className="text-[11px] font-black uppercase tracking-widest text-brand-grey08 leading-relaxed">
            Location verification is powered by Gemini 2.5 Flash with live Google Maps grounding.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ProfileGeneratorModal;
